/**
 * Picking models up in VR.
 *
 * The whole mechanism is `Object3D.attach`, which re-parents while preserving the world
 * transform. Attach a model to the controller and it stays exactly where it was but now
 * moves and turns with the hand; attach it back to the scene on release and it stays
 * exactly where the hand left it. No offset bookkeeping, no drift.
 *
 * Several models can be in the room at once, and each hand holds at most one of them, so
 * both hands can be moving different models at the same time. That is the whole of what
 * "independently" means here: there is no shared transform anywhere for one model's
 * movement to disturb another through.
 *
 * This class deliberately knows nothing about WebXR — it takes plain `Object3D`s — so the
 * state machine can be tested without a headset or a browser.
 */

import { Box3, type Object3D, Vector3 } from 'three';

/**
 * How far outside a model's bounding box still counts as a grab, in world metres.
 *
 * Generous on purpose. Judging depth against a small object through a headset is harder
 * than it sounds, and a grab that misses is more annoying than one that over-reaches.
 */
const DEFAULT_REACH_M = 0.08;

/**
 * Smallest grabbable region, in world metres.
 *
 * Without this, a 5 mm model would need the controller inside a 5 mm box, which is not a
 * gesture anyone can reliably make.
 */
const MIN_GRAB_EXTENT_M = 0.06;

export interface GrabOptions {
  readonly reach?: number;
  readonly minExtent?: number;
}

export class GrabController {
  /** Which model each hand is holding. A hand absent from the map is holding nothing. */
  private readonly held = new Map<Object3D, Object3D>();

  private targets: readonly Object3D[] = [];

  private readonly reach: number;
  private readonly minExtent: number;

  private readonly box = new Box3();
  private readonly controllerPosition = new Vector3();
  private readonly centre = new Vector3();
  private readonly size = new Vector3();
  private readonly padding = new Vector3();

  /**
   * @param home Where models live when nobody is holding them.
   */
  constructor(
    private readonly home: Object3D,
    options: GrabOptions = {},
  ) {
    this.reach = options.reach ?? DEFAULT_REACH_M;
    this.minExtent = options.minExtent ?? MIN_GRAB_EXTENT_M;
  }

  /**
   * Declare what there is to pick up.
   *
   * Anything dropped from the list that a hand was holding is let go of first, so a model
   * taken out of the room while it is in someone's hand does not stay parented to a
   * controller — which would leave it following that hand around invisibly, and put it
   * back in the scene at a surprising place if it were ever shown again.
   */
  setTargets(targets: readonly Object3D[]): void {
    this.targets = [...targets];

    for (const [controller, target] of this.held) {
      if (this.targets.includes(target)) continue;
      this.home.attach(target);
      this.held.delete(controller);
    }
  }

  get anyHeld(): boolean {
    return this.held.size > 0;
  }

  /** What this hand is holding, if anything. */
  heldBy(controller: Object3D): Object3D | undefined {
    return this.held.get(controller);
  }

  isHeld(target: Object3D): boolean {
    for (const held of this.held.values()) if (held === target) return true;
    return false;
  }

  /** True if the controller is close enough to this model to pick it up. */
  isWithinReach(controller: Object3D, target: Object3D): boolean {
    if (!this.grabBox(target)) return false;

    controller.updateMatrixWorld(true);
    this.controllerPosition.setFromMatrixPosition(controller.matrixWorld);
    return this.box.containsPoint(this.controllerPosition);
  }

  /**
   * Attempt to pick something up with this controller.
   *
   * Where a hand is in reach of more than one model — easily done, since the reach
   * padding is deliberately generous — the nearest one wins, measuring to each model's
   * centre. Nearest is what "reach for that one" means to the person doing it, and it is
   * the only rule that stays predictable as models are moved around next to each other.
   *
   * A model already held by the *other* hand is a candidate like any other, so reaching
   * into it takes it over. That is what people expect when passing something between
   * their hands, and it costs nothing to allow.
   *
   * @returns the model taken hold of, or undefined if nothing was within reach.
   */
  tryGrab(controller: Object3D): Object3D | undefined {
    const already = this.held.get(controller);
    if (already) return already;

    const target = this.nearestWithinReach(controller);
    if (!target) return undefined;

    // Whichever hand had it loses it. Left in place, that hand would keep a claim on a
    // model that is no longer parented to it, and its release would then reparent a model
    // out of the other hand.
    for (const [hand, held] of this.held) {
      if (held === target) this.held.delete(hand);
    }

    controller.attach(target);
    this.held.set(controller, target);
    return target;
  }

  /** Let go, if this controller is holding anything. Returns true if it released. */
  release(controller: Object3D): boolean {
    const target = this.held.get(controller);
    if (!target) return false;

    this.home.attach(target);
    this.held.delete(controller);
    return true;
  }

  /** Let go of everything, whichever hand is holding it, for session end and teardown. */
  releaseAll(): void {
    for (const [controller, target] of this.held) {
      this.home.attach(target);
      this.held.delete(controller);
    }
  }

  private nearestWithinReach(controller: Object3D): Object3D | undefined {
    controller.updateMatrixWorld(true);
    this.controllerPosition.setFromMatrixPosition(controller.matrixWorld);

    let best: Object3D | undefined;
    let bestDistance = Infinity;

    for (const target of this.targets) {
      if (!this.grabBox(target)) continue;
      if (!this.box.containsPoint(this.controllerPosition)) continue;

      this.box.getCenter(this.centre);
      const distance = this.centre.distanceToSquared(this.controllerPosition);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = target;
      }
    }

    return best;
  }

  /**
   * Set `this.box` to the model's grabbable volume, or report that it has none.
   *
   * Padded by the reach distance, then by whatever more it takes to meet the minimum
   * grabbable size — so tiny models get a usable target without inflating large ones.
   */
  private grabBox(target: Object3D): boolean {
    target.updateMatrixWorld(true);
    this.box.setFromObject(target);
    if (this.box.isEmpty()) return false;

    this.box.getSize(this.size);
    this.padding.set(
      this.reach + Math.max(0, this.minExtent - this.size.x) / 2,
      this.reach + Math.max(0, this.minExtent - this.size.y) / 2,
      this.reach + Math.max(0, this.minExtent - this.size.z) / 2,
    );
    this.box.expandByVector(this.padding);
    return true;
  }
}
